"use client";

/**
 * P3-17 · Unified recent-search history. Guests use localStorage (P3-15); a
 * signed-in shopper uses the account-backed `SearchHistory` table via tRPC. On
 * sign-in, the guest's local terms are merged into the account once, then the
 * local copy is cleared — so history follows the shopper across devices.
 *
 * Returns the same `{ recent, add, remove, clear }` shape as the guest hook, so
 * the overlay consumes one interface regardless of auth.
 */
import * as React from "react";

import { api } from "~/trpc/react";
import {
  clearGuestHistory,
  readGuestHistory,
  useRecentSearches,
  type RecentSearches,
} from "~/lib/search/use-recent-searches";

export function useSearchHistory({
  signedIn,
  enabled = true,
}: {
  signedIn: boolean;
  enabled?: boolean;
}): RecentSearches {
  // Guest store — active only when signed out (and history is enabled).
  const guest = useRecentSearches(enabled && !signedIn);

  const utils = api.useUtils();
  const invalidate = () => void utils.search.history.list.invalidate();

  const accountList = api.search.history.list.useQuery(undefined, {
    enabled: signedIn && enabled,
    staleTime: 30_000,
  });
  const recordM = api.search.history.record.useMutation({ onSuccess: invalidate });
  const removeM = api.search.history.remove.useMutation({ onSuccess: invalidate });
  const clearM = api.search.history.clear.useMutation({ onSuccess: invalidate });
  const importM = api.search.history.import.useMutation({ onSuccess: invalidate });

  // One-shot guest → account migration on sign-in.
  const migrated = React.useRef(false);
  React.useEffect(() => {
    if (!signedIn || !enabled || migrated.current) return;
    migrated.current = true;
    const terms = readGuestHistory();
    if (terms.length === 0) return;
    importM.mutate(
      { terms },
      { onSuccess: () => clearGuestHistory() },
    );
    // importM is stable across renders; excluded to keep this a one-shot effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, enabled]);

  if (!signedIn) return guest;

  return {
    recent: enabled ? (accountList.data ?? []) : [],
    add: (term) => {
      if (enabled) recordM.mutate({ term });
    },
    remove: (term) => removeM.mutate({ term }),
    clear: () => clearM.mutate(),
  };
}
